/**
 * Cognito SRP (Secure Remote Password) — the USER_SRP_AUTH exchange.
 *
 * The dev User Pool app client enables only ALLOW_USER_SRP_AUTH (no
 * USER_PASSWORD_AUTH, no client secret), so sign-in must speak SRP. Rather
 * than pull in Amplify (bundle cost, and AUTH-DESIGN.md says no Amplify), this
 * implements the SRP-6a math Cognito expects, using isomorphic primitives:
 * Web Crypto (SHA-256 / HMAC) + native BigInt. The same code runs in the
 * browser and under Vitest/Node.
 *
 * The algorithm mirrors amazon-cognito-identity-js's AuthenticationHelper.
 * Correctness is validated end-to-end against live Cognito (a wrong signature
 * is rejected by the server), which is the authoritative oracle for hand-rolled
 * crypto — see the auth e2e spec.
 */

// 3072-bit MODP group (RFC 5054) used by Cognito.
const N_HEX =
  'FFFFFFFFFFFFFFFFC90FDAA22168C234C4C6628B80DC1CD1' +
  '29024E088A67CC74020BBEA63B139B22514A08798E3404DD' +
  'EF9519B3CD3A431B302B0A6DF25F14374FE1356D6D51C245' +
  'E485B576625E7EC6F44C42E9A637ED6B0BFF5CB6F406B7ED' +
  'EE386BFB5A899FA5AE9F24117C4B1FE649286651ECE45B3D' +
  'C2007CB8A163BF0598DA48361C55D39A69163FA8FD24CF5F' +
  '83655D23DCA3AD961C62F356208552BB9ED529077096966D' +
  '670C354E4ABC9804F1746C08CA18217C32905E462E36CE3B' +
  'E39E772C180E86039B2783A2EC07A28FB5C55DF06F4C52C9' +
  'DE2BCBF6955817183995497CEA956AE515D2261898FA0510' +
  '15728E5A8AAAC42DAD33170D04507A33A85521ABDF1CBA64' +
  'ECFB850458DBEF0A8AEA71575D060C7DB3970F85A6E1E4C7' +
  'ABF5AE8CDB0933D71E8C94E04A25619DCEE3D2261AD2EE6B' +
  'F12FFA06D98A0864D87602733EC86A64521F2B18177B200C' +
  'BBE117577A615D6C770988C0BAD946E208E24FA074E5AB31' +
  '43DB5BFCE0FD108E4B82D120A93AD2CAFFFFFFFFFFFFFFFF';

const N = BigInt('0x' + N_HEX);
const g = 2n;
const INFO_BITS = 'Caldera Derived Key';

const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const encoder = new TextEncoder();

function bytesToHex(bytes: Uint8Array): string {
  let hex = '';
  for (const byte of bytes) hex += byte.toString(16).padStart(2, '0');
  return hex;
}

function hexToBytes(hex: string): Uint8Array {
  const clean = hex.length % 2 === 1 ? '0' + hex : hex;
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (const byte of bytes) bin += String.fromCharCode(byte);
  return btoa(bin);
}

function concatBytes(...parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

/**
 * Pad a value to the even-length, sign-safe hex Cognito expects: prefix a
 * leading zero-byte when the high nibble is set so the value stays positive.
 */
export function padHex(value: bigint): string {
  let hex = value.toString(16);
  if (hex.length % 2 === 1) {
    hex = '0' + hex;
  } else if ('89abcdef'.includes(hex[0]!)) {
    hex = '00' + hex;
  }
  return hex;
}

async function sha256(bytes: Uint8Array): Promise<Uint8Array> {
  const digest = await crypto.subtle.digest('SHA-256', bytes as BufferSource);
  return new Uint8Array(digest);
}

async function hmacSha256(key: Uint8Array, data: Uint8Array): Promise<Uint8Array> {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key as BufferSource,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await crypto.subtle.sign('HMAC', cryptoKey, data as BufferSource);
  return new Uint8Array(sig);
}

/** SHA-256 over the BYTES a hex string represents, returned as hex. */
async function hexHash(hex: string): Promise<string> {
  return bytesToHex(await sha256(hexToBytes(hex)));
}

function modPow(base: bigint, exp: bigint, mod: bigint): bigint {
  let b = ((base % mod) + mod) % mod;
  let e = exp;
  let result = 1n;
  while (e > 0n) {
    if (e & 1n) result = (result * b) % mod;
    e >>= 1n;
    b = (b * b) % mod;
  }
  return result;
}

function randomBigInt(byteLength: number): bigint {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return BigInt('0x' + bytesToHex(bytes));
}

/** UTC timestamp in the exact `ddd MMM D HH:mm:ss UTC yyyy` en-US format. */
export function cognitoTimestamp(now: Date = new Date()): string {
  const weekDay = WEEK_DAYS[now.getUTCDay()];
  const month = MONTHS[now.getUTCMonth()];
  const day = now.getUTCDate(); // NOT zero-padded, per Cognito
  const hh = String(now.getUTCHours()).padStart(2, '0');
  const mm = String(now.getUTCMinutes()).padStart(2, '0');
  const ss = String(now.getUTCSeconds()).padStart(2, '0');
  return `${weekDay} ${month} ${day} ${hh}:${mm}:${ss} UTC ${now.getUTCFullYear()}`;
}

export interface SrpChallenge {
  /** Internal username from ChallengeParameters.USER_ID_FOR_SRP. */
  userIdForSrp: string;
  password: string;
  /** ChallengeParameters.SALT (hex). */
  saltHex: string;
  /** ChallengeParameters.SRP_B (hex). */
  srpBHex: string;
  /** ChallengeParameters.SECRET_BLOCK (base64). */
  secretBlock: string;
  /** User Pool id suffix (the part after `_`), e.g. `gNqUwYZkc`. */
  poolName: string;
}

export interface SrpSignature {
  timestamp: string;
  /** base64 PASSWORD_CLAIM_SIGNATURE. */
  signature: string;
}

/**
 * An SRP client instance holds the ephemeral secret `a` and public `A` for one
 * sign-in attempt. `srpA` is sent in InitiateAuth; `sign()` answers the
 * PASSWORD_VERIFIER challenge.
 */
export class SrpClient {
  private readonly a: bigint;
  readonly largeA: bigint;

  constructor(smallA?: bigint) {
    // 128 random bytes reduced mod N, regenerated until A mod N != 0.
    let a = smallA ?? randomBigInt(128) % N;
    let largeA = modPow(g, a, N);
    while (largeA % N === 0n) {
      a = randomBigInt(128) % N;
      largeA = modPow(g, a, N);
    }
    this.a = a;
    this.largeA = largeA;
  }

  /** Hex `SRP_A` for the InitiateAuth AuthParameters. */
  get srpA(): string {
    return this.largeA.toString(16);
  }

  async sign(
    challenge: SrpChallenge,
    now: Date = new Date(),
  ): Promise<SrpSignature> {
    const serverB = BigInt('0x' + challenge.srpBHex);
    if (serverB % N === 0n) {
      throw new Error('SRP error: server B mod N is zero');
    }
    const salt = BigInt('0x' + challenge.saltHex);

    const k = BigInt('0x' + (await hexHash(padHex(N) + padHex(g))));
    const u = BigInt('0x' + (await hexHash(padHex(this.largeA) + padHex(serverB))));
    if (u === 0n) {
      throw new Error('SRP error: u is zero');
    }

    const usernamePassword = `${challenge.poolName}${challenge.userIdForSrp}:${challenge.password}`;
    const usernamePasswordHash = bytesToHex(
      await sha256(encoder.encode(usernamePassword)),
    );
    const x = BigInt('0x' + (await hexHash(padHex(salt) + usernamePasswordHash)));

    const gModPowX = modPow(g, x, N);
    const base = serverB - k * gModPowX;
    const exponent = this.a + u * x;
    const s = modPow(base, exponent, N);

    // HKDF: PRK = HMAC(salt=U, ikm=S); key = HMAC(PRK, "Caldera Derived Key"\x01)[:16]
    const prk = await hmacSha256(hexToBytes(padHex(u)), hexToBytes(padHex(s)));
    const infoBits = concatBytes(encoder.encode(INFO_BITS), Uint8Array.of(1));
    const hkdf = (await hmacSha256(prk, infoBits)).slice(0, 16);

    const timestamp = cognitoTimestamp(now);
    const message = concatBytes(
      encoder.encode(challenge.poolName),
      encoder.encode(challenge.userIdForSrp),
      base64ToBytes(challenge.secretBlock),
      encoder.encode(timestamp),
    );
    const signature = bytesToBase64(await hmacSha256(hkdf, message));

    return { timestamp, signature };
  }
}
